<template>
  <v-navigation-drawer
    v-model="value"
    v-click-outside="{
      handler: null,
      closeConditional,
    }"
    app
    temporary
    right
    width="975"
  >
    <v-container fluid>
      <!-- Header -->
      <v-row class="primary white--text">
        <v-col cols="11">
          <CaseHeader :selectedCase="localCase" />
        </v-col>
        <v-spacer />
        <v-col>
          <v-btn
            @click.stop="onClose"
            fab
            x-small
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Body -->
      <v-row>
        <v-tabs v-model="tab">
          <v-tab>Case Details</v-tab>
          <v-tab>
            Case Reports and History
            <span v-if="unresolvedCaseReports > 0">
              <v-chip
                color="red lighten-1"
                text-color="white"
                small
                class="ml-1"
              >
                {{ unresolvedCaseReports }} Open
              </v-chip>
            </span>
          </v-tab>
        </v-tabs>
        <v-container>
          <v-tabs-items v-model="tab">
            <!-- Details Tab -->
            <v-tab-item>
              <v-row dense>
                <!-- General - Person -->
                <v-col
                  cols="5"
                  v-if="localCase.type != 'vessel' && localCase.type != 'organization'"
                >
                  <v-card
                    outlined
                    class="fill-height"
                  >
                    <v-card-title> General </v-card-title>
                    <v-card-text>
                      <v-row>
                        <!-- DOB -->
                        <v-col cols="4">
                          <div class="overline font-weight-bold">DOB</div>
                          {{ localCase.dob ? localCase.dob : "N/A" }}
                        </v-col>

                        <!-- POB -->
                        <v-col cols="4">
                          <div class="overline font-weight-bold">Birthplace</div>
                          {{ localCase.placeOfBirth ? localCase.placeOfBirth : "N/A" }}
                        </v-col>

                        <!-- Citizenship -->
                        <v-col cols="4">
                          <div class="overline font-weight-bold">Citizenship</div>
                          {{ localCase.citizenship ? localCase.citizenship : "N/A" }}
                        </v-col>

                        <!-- Nationality -->
                        <v-col cols="4">
                          <div class="overline font-weight-bold">Nationality</div>
                          {{ localCase.nationality ? localCase.nationality : "N/A" }}
                        </v-col>

                        <!-- Gender -->
                        <v-col cols="4">
                          <div class="overline font-weight-bold">Gender</div>
                          {{ localCase.gender ? localCase.gender : "N/A" }}
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Contact Info -->
                <v-col>
                  <v-card outlined>
                    <v-card-title>Contact</v-card-title>
                    <v-card-text>
                      <v-row>
                        <!-- Address  -->
                        <v-col cols="6">
                          <div class="overline font-weight-bold">Address</div>
                          <span v-if="addressBlank(localCase.address)"> N/A </span>
                          <div
                            v-else
                            cols="6"
                          >
                            {{ localCase.address.address1 }}
                            {{ localCase.address.address2 }}
                            {{ localCase.address.address3 }}
                            {{ localCase.address.address4 }}
                            <br />
                            {{ localCase.address.city }}
                            {{ localCase.address.stateOrProvince }}
                            {{ localCase.address.postalCode }}
                            <br />
                            {{ localCase.address.country }}
                          </div>
                        </v-col>

                        <!-- Phone  -->
                        <v-col cols="6">
                          <div class="overline font-weight-bold">Phone Number</div>
                          <span v-if="selectedCase.phoneNumber">
                            {{ localCase.phoneNumber }}
                          </span>
                          <span v-else>N/A</span>
                        </v-col>

                        <!-- Email -->
                        <v-col cols="6">
                          <div class="overline font-weight-bold">Email</div>
                          {{ localCase.email ? localCase.email : "N/A" }}
                        </v-col>

                        <!-- Website -->
                        <v-col cols="6">
                          <div class="overline font-weight-bold">Website</div>
                          {{ localCase.website ? localCase.website : "N/A" }}
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- IDs -->
              <v-row dense>
                <v-col>
                  <v-card outlined>
                    <v-card-title>IDs, Licenses and Documents</v-card-title>
                    <v-card-text>
                      <template v-if="!localCase.identification?.length > 0"> None </template>
                      <template v-else>
                        <v-row>
                          <template v-for="(identification, index) in localCase.identification">
                            <v-col
                              cols="4"
                              :key="index"
                            >
                              <ID
                                :type="identification.type"
                                :description="identification.description"
                                :number="identification.idNumber"
                                :country="identification.country"
                                :issued="identification.issueDate"
                                :expiration="identification.expirationDate"
                              />
                            </v-col>
                          </template>
                        </v-row>
                      </template>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Custom fields  -->
              <v-row v-if="localCase.customFields && Object.keys(localCase.customFields).length > 0">
                <v-col>
                  <v-card outlined>
                    <v-card-title>Other Information</v-card-title>
                    <v-card-text>
                      <v-row dense>
                        <v-col
                          v-for="(value, propertyName) in localCase.customFields"
                          :key="propertyName"
                          cols="12"
                        >
                          <div class="overline font-weight-bold">
                            {{ propertyName }}
                          </div>
                          {{ value }}
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Case Reports Tab -->
            <v-tab-item>
              <v-data-table
                dense
                :headers="caseReportHeaders"
                :items="localCase ? localCase.caseReports : {}"
              >
                <template v-slot:item.reportDate="{ item }">
                  {{ formatDateTime(item.reportDate) }}
                </template>

                <template v-slot:item.status="{ item }">
                  <ResolutionStatusChips :resolutionStatus="item.status" />
                </template>

                <template v-slot:item.openCaseReport="{ item }">
                  <v-btn
                    icon
                    x-small
                    color="primary"
                    @click="openCaseReport(item)"
                  >
                    <v-icon dark>mdi-export</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-tab-item>
          </v-tabs-items>
        </v-container>
      </v-row>
    </v-container>
  </v-navigation-drawer>
</template>

<script>
import { formatDateTime } from "../../utils/dateUtils";
import { getCaseGroupName } from "../../utils/caseGroupUtils";
import { hasOpenCaseReports } from "../../utils/caseUtils";
import ResolutionStatusChips from "../../components/ResolutionStatusChips";
import CaseHeader from "../../components/case/CaseHeader";
import ID from "../../components/ID.vue";
export default {
  name: "ViewCase",
  components: { ResolutionStatusChips, CaseHeader, ID },
  data() {
    return {
      visible: false,
      tab: null,
      localCase: {},
      caseReportHeaders: [
        { text: "Sanction Target", value: "match.sanction.name" },
        { text: "Sanction List", value: "match.sanction.source" },
        { text: "Date", value: "reportDate" },
        { text: "Status", value: "status" },
        { text: "Assignee", value: "assignee" },
        { text: "View", value: "openCaseReport" },
      ],
    };
  },
  props: {
    selectedCase: {
      type: Object,
      required: true,
    },
    value: Boolean,
  },
  watch: {
    selectedCase: {
      // This listens for updates made in the Case Report modal that are sent back to the Case Manager view.
      deep: true, // Watch for nested changes within the object
      immediate: true, // Run the handler immediately on component creation
      handler(newCase) {
        this.localCase = { ...newCase }; // Shallow copy the object
      },
    },
  },
  computed: {
    unresolvedCaseReports() {
      return this.hasOpenCaseReports(this.selectedCase);
    },
  },
  methods: {
    formatDateTime,
    getCaseGroupName,
    hasOpenCaseReports,
    openCaseReport(caseReport) {
      this.$emit("openCaseReport", caseReport); // Goes to Casemanager
    },
    getPassportString(passport) {
      if (!passport || Object.keys(passport).length === 0) {
        return "NA";
      } else {
        let passportString = "";
        if (passport.passportNumber) {
          passportString += passport.passportNumber + " ";
        }
        if (passport.passportCountry) {
          passportString += passport.passportCountry;
        }
        return passportString;
      }
    },
    getDLString(dl) {
      let dlString = "";
      if (dl.licenseNumber) {
        dlString += dl.licenseNumber + " ";
        if (dl.licenseCountry) {
          dlString += dl.licenseCountry;
        }
      }
      return dlString;
    },
    addressBlank(address) {
      if (address) {
        return ![address.address1, address.address2, address.address3, address.address4, address.city, address.stateOrProvince, address.postalCode, address.country].some(Boolean);
      } else {
        return true;
      }
    },
    closeConditional() {
      return false;
    },
    onClose() {
      this.$emit("onClose");
    },
  },
};
</script>
